import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import SEO from "@/components/SEO";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Chronicle() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category") || "";

  useEffect(() => {
    api.get("/chronicle/categories").then((r) => setCategories(r.data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = activeCategory ? { category: activeCategory } : {};
    api
      .get("/chronicle", { params: q })
      .then((r) => setPosts(r.data || []))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const [hero, ...rest] = posts;
  const categoryLabel = (key) =>
    categories.find((c) => c.key === key)?.label || key;

  const setCategory = (key) => {
    if (key) setParams({ category: key });
    else setParams({});
  };

  return (
    <div data-testid="chronicle-page" className="bg-[#F9F9F4] text-[#1A3626]">
      <SEO
        title="The Cabbage Chronicle"
        path="/chronicle"
        description="The Cabbage Chronicle — field notes, division updates and underdog reports from the Wet Cabbage universe."
      />

      {/* MASTHEAD */}
      <section className="bg-[#F9F9F4]" data-testid="chronicle-masthead">
        <div className="mx-auto max-w-[1400px] px-6 pb-12 pt-20 md:px-10 md:pb-20 md:pt-32">
          <div className="font-display text-[10px] font-bold uppercase tracking-[0.42em] text-[#93C065] md:text-xs">
            Issue ongoing
          </div>
          <h1
            className="font-display mt-5 text-5xl font-black uppercase leading-[0.88] tracking-tight sm:text-7xl md:text-8xl lg:text-[120px]"
            data-testid="chronicle-headline"
          >
            The Cabbage
            <br />
            Chronicle.
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#1A3626] md:text-xl md:leading-[1.6]">
            Field notes, division updates and overconfident reports from inside
            the Wet Cabbage universe. Edited slowly. Researched seriously.
          </p>
        </div>
      </section>

      {/* CATEGORY FILTERS */}
      {categories.length > 0 && (
        <section
          className="border-y border-[#1A3626]/15 bg-[#F9F9F4]"
          data-testid="chronicle-filters"
        >
          <div className="mx-auto max-w-[1400px] overflow-x-auto px-6 py-4 md:px-10">
            <div className="flex min-w-max items-center gap-2">
              <button
                type="button"
                onClick={() => setCategory("")}
                className={`font-display whitespace-nowrap border-2 border-[#1A3626] px-4 py-2 text-[10px] font-black uppercase tracking-[0.28em] transition-all md:text-xs ${
                  !activeCategory
                    ? "bg-[#1A3626] text-[#F9F9F4]"
                    : "bg-transparent text-[#1A3626] hover:bg-[#EBE7DD]"
                }`}
                data-testid="chronicle-cat-all"
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  className={`font-display whitespace-nowrap border-2 border-[#1A3626] px-4 py-2 text-[10px] font-black uppercase tracking-[0.28em] transition-all md:text-xs ${
                    activeCategory === c.key
                      ? "bg-[#1A3626] text-[#F9F9F4]"
                      : "bg-transparent text-[#1A3626] hover:bg-[#EBE7DD]"
                  }`}
                  data-testid={`chronicle-cat-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HERO + GRID */}
      <section className="bg-[#F9F9F4]" data-testid="chronicle-grid">
        <div className="mx-auto max-w-[1400px] px-6 pb-24 pt-12 md:px-10 md:pb-32 md:pt-20">
          {loading ? (
            <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-[3/4] animate-pulse border-2 border-[#1A3626] bg-[#EBE7DD]"
                />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="border-2 border-dashed border-[#1A3626] p-20 text-center">
              <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/60 md:text-xs">
                Issue in progress
              </div>
              <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#1A3626]/80 md:text-lg">
                The Chronicle is currently being edited slowly by one curator
                and a dog named Mush. Check back soon.
              </p>
            </div>
          ) : (
            <>
              {hero && <HeroPost post={hero} categoryLabel={categoryLabel(hero.category)} />}

              {rest.length > 0 && (
                <div className="mt-16 grid grid-cols-1 gap-x-10 gap-y-16 md:mt-24 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-12 lg:gap-y-20">
                  {rest.map((p) => (
                    <PostCard
                      key={p.id}
                      post={p}
                      categoryLabel={categoryLabel(p.category)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function HeroPost({ post, categoryLabel }) {
  return (
    <Link
      to={`/chronicle/${post.slug}`}
      className="group block"
      data-testid="chronicle-hero-post"
    >
      <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-14 lg:gap-20">
        <div className="md:col-span-7">
          <div className="aspect-[4/3] overflow-hidden bg-[#EBE7DD]">
            {post.hero_image_url ? (
              <img
                src={fileUrl(post.hero_image_url)}
                alt={post.hero_image_alt || post.title}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-display text-xs uppercase tracking-[0.3em] text-[#1A3626]/40">
                {categoryLabel}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col justify-center md:col-span-5">
          <div className="font-display text-[10px] font-black uppercase tracking-[0.42em] text-[#93C065] md:text-xs">
            {categoryLabel}
          </div>
          <h2 className="font-display mt-5 text-3xl font-black uppercase leading-[0.95] tracking-tight text-[#1A3626] sm:text-4xl md:text-5xl lg:text-6xl">
            {post.title}
          </h2>
          {post.excerpt && (
            <p className="mt-5 max-w-md text-base leading-relaxed text-[#1A3626]/85 md:text-lg md:leading-[1.65]">
              {post.excerpt}
            </p>
          )}
          <div className="mt-6 flex items-center gap-3 text-xs text-[#1A3626]/60">
            <span>{post.author}</span>
            <span aria-hidden>·</span>
            <span>{formatDate(post.published_at)}</span>
          </div>
          <div className="font-display mt-7 inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.36em] text-[#1A3626] transition-all group-hover:gap-3 md:text-xs">
            Read the dispatch
            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
          </div>
        </div>
      </div>
    </Link>
  );
}

function PostCard({ post, categoryLabel }) {
  return (
    <Link
      to={`/chronicle/${post.slug}`}
      className="group block"
      data-testid={`chronicle-card-${post.slug}`}
    >
      <div className="aspect-[4/3] overflow-hidden bg-[#EBE7DD]">
        {post.hero_image_url ? (
          <img
            src={fileUrl(post.hero_image_url)}
            alt={post.hero_image_alt || post.title}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-display text-xs uppercase tracking-[0.3em] text-[#1A3626]/40">
            {categoryLabel}
          </div>
        )}
      </div>
      <div className="mt-5">
        <div className="font-display text-[10px] font-black uppercase tracking-[0.42em] text-[#93C065] md:text-xs">
          {categoryLabel}
        </div>
        <h3 className="font-display mt-3 text-xl font-black uppercase leading-[1.05] tracking-tight text-[#1A3626] md:text-2xl lg:text-3xl">
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="mt-3 text-base leading-relaxed text-[#1A3626]/80 md:leading-[1.65]">
            {post.excerpt}
          </p>
        )}
        <div className="mt-4 flex items-center gap-3 text-xs text-[#1A3626]/60">
          <span>{post.author}</span>
          <span aria-hidden>·</span>
          <span>{formatDate(post.published_at)}</span>
        </div>
      </div>
    </Link>
  );
}
